import React, { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { PrePurchaseCard, PostPurchaseCard, AppraisedVehicleCard, PreOrderCard } from './GarageCardTypes';
import {
  AppraisalShowText,
  AppraisalsViewLinkWrapper,
  Arrow,
  HeaderText,
  RedirectLinkStyled,
} from './GarageCard.styles';
import OVPCard from '../ovp-card-selector/OVPCard';
import { AnalyticEvents, AppRoute } from '../../constants';
import { Analytics } from '../../services/analytics-service';

const UpArrow = () => (
  <Arrow width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 8L7 1L1 8" stroke="#00AED9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Arrow>
);

const DownArrow = () => (
  <Arrow width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L7 8L13 1" stroke="#00AED9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Arrow>
);

const GarageCard = (props: {
  prePurchaseVehicles: any;
  preOrderVehicles: any;
  postPurchaseVehicles: any;
  appraisals: any;
  ovpData: any;
  ovpFeed: any;
}) => {
  const [showMoreVehicles, setShowMoreVehicles] = React.useState(false);

  const { prePurchaseVehicles, preOrderVehicles, postPurchaseVehicles, appraisals, ovpData, ovpFeed } = props;

  // Event handler called on garage card click
  const trackEventHandler = (eventProps: any) => {
    Analytics.trackEvent(AnalyticEvents.garageVehicleCardClicked, eventProps);
    datadogRum.addAction(AnalyticEvents.garageVehicleCardClicked, eventProps);
  };

  const captureImpression = (eventProps: any) => {
    Analytics.trackEvent(AnalyticEvents.garageVehicleCardImpression, eventProps);
    datadogRum.addAction(AnalyticEvents.garageVehicleCardImpression, eventProps);
  };

  const appraisalCards = appraisals;
  const maxVisibleCars = 5;

  const appraisalCount = appraisalCards?.length;
  const visibleAppraisalArray = appraisalCards?.slice(1, maxVisibleCars);

  const hasMoreThanOneAppraisal = appraisalCards?.length > 1;

  const hasTooManyCars = appraisalCards?.length > maxVisibleCars;

  const [filteredOvpFeed, setFilteredOvpFeed] = useState([]);

  useEffect(() => {
    const filterFeedCards = async () => {
      const filterList: string[] = ['DigitalGlovebox'];

      const filteredData = ovpFeed?.filter((ovpItem: any) => filterList.some((type: string) => type === ovpItem.type));
      setFilteredOvpFeed(filteredData);
    };

    filterFeedCards();
  }, [ovpFeed]);

  const userState = {
    ...ovpData,
    ...{ hostEnvironment: process.env.NODE_ENV },
  };

  const toggleCollapseHandler = () => {
    if (!showMoreVehicles) {
      Analytics.trackEvent(AnalyticEvents.appraisedGarageExpand);
      datadogRum.addAction(AnalyticEvents.appraisedGarageExpand);
    } else {
      Analytics.trackEvent(AnalyticEvents.appraisedGarageCollapse);
      datadogRum.addAction(AnalyticEvents.appraisedGarageCollapse);
    }
    setShowMoreVehicles(() => !showMoreVehicles);
  };

  return (
    <div>
      {preOrderVehicles && preOrderVehicles.length > 0 && (
        <>
          <HeaderText data-testid="header-text-preorder">Pre-Ordered</HeaderText>
          {preOrderVehicles?.map((vehicleInfo: any) => (
            <PreOrderCard
              key={vehicleInfo.vehicle?.id}
              vehicleInfo={vehicleInfo}
              trackEventHandler={trackEventHandler}
              captureImpression={captureImpression}
            />
          ))}
        </>
      )}

      {prePurchaseVehicles && prePurchaseVehicles.length > 0 && (
        <>
          <HeaderText data-testid="header-text-prepurchase">Purchase In Progress</HeaderText>
          {prePurchaseVehicles?.map((vehicleInfo: any) => (
            <PrePurchaseCard
              key={vehicleInfo.vehicle?.id}
              vehicleInfo={vehicleInfo}
              trackEventHandler={trackEventHandler}
              captureImpression={captureImpression}
            />
          ))}
          {filteredOvpFeed?.map((feedCard: any, index: any) => (
            <OVPCard
              key={`${feedCard.type}${index}`}
              feedCard={feedCard}
              positionValue={index + 1}
              userState={userState}
              vehicleId={prePurchaseVehicles[index].vehicle?.id}
            />
          ))}
        </>
      )}

      {postPurchaseVehicles && postPurchaseVehicles.length > 0 && (
        <>
          <HeaderText data-testid="header-text-purchased">Purchased From Carvana</HeaderText>
          {postPurchaseVehicles?.map((vehicleInfo: any) => (
            <PostPurchaseCard
              key={vehicleInfo.vehicle.id}
              vehicleInfo={vehicleInfo}
              trackEventHandler={trackEventHandler}
              captureImpression={captureImpression}
            />
          ))}
        </>
      )}

      {appraisalCards && appraisalCards.length > 0 && (
        <>
          <HeaderText data-testid="header-text-appraised">Appraised Vehicles {`(${appraisalCount})`}</HeaderText>
          <AppraisedVehicleCard
            key={appraisalCards[0].vehicle.id}
            vehicleInfo={appraisalCards[0]}
            trackEventHandler={trackEventHandler}
            captureImpression={captureImpression}
          />

          {showMoreVehicles &&
            visibleAppraisalArray?.map((appraisal: any) => (
              <AppraisedVehicleCard
                key={appraisal.vehicle.id}
                vehicleInfo={appraisal}
                trackEventHandler={trackEventHandler}
                captureImpression={captureImpression}
              />
            ))}

          <AppraisalsViewLinkWrapper>
            {hasMoreThanOneAppraisal && (
              <>
                <AppraisalShowText data-testid="appraisal-view-more-less-toggle" onClick={toggleCollapseHandler}>
                  {!showMoreVehicles ? <DownArrow /> : <UpArrow />}
                  {showMoreVehicles ? 'Show Less' : 'Show More'}
                </AppraisalShowText>
              </>
            )}

            {hasTooManyCars && (
              <>
                <RedirectLinkStyled
                  data-testid="appraisal-view-all-link"
                  onClick={() => {
                    window.location.pathname = AppRoute.SELL_MY_CAR;
                  }}
                  style={{ textDecoration: 'underline' }}
                >
                  View All Appraised Vehicles
                </RedirectLinkStyled>
              </>
            )}
          </AppraisalsViewLinkWrapper>
        </>
      )}
    </div>
  );
};

export default GarageCard;
